import { useState, useEffect, useRef } from 'react';
import { Helmet } from 'react-helmet-async';
import { useNavigate } from 'react-router-dom';
import {
  Loader2,
  AlertCircle,
  Send,
  CheckCircle,
  Clock,
} from 'lucide-react';
import ProviderLayout from '@/components/layout/ProviderLayout';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { useAuth } from '@/hooks/useAuth';
import { useToast } from '@/hooks/use-toast';
import apiService from '@/services/api';

interface Payout {
  id: number;
  amount_pence: number;
  amount_gbp: string;
  percentage: number;
  status: 'pending' | 'approved' | 'processing' | 'paid' | 'failed';
  paid_at: string | null;
  created_at: string;
  updated_at: string;
}

const ProviderPayoutsPage = () => {
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [payouts, setPayouts] = useState<Payout[]>([]);
  const [loading, setLoading] = useState(false);
  const [filterStatus, setFilterStatus] = useState<string>('all');
  const hasCheckedAuth = useRef(false);

  useEffect(() => {
    if (!authLoading && hasCheckedAuth.current === false) {
      hasCheckedAuth.current = true;
      if (!user || user.role !== 'provider') {
        navigate('/provider/login');
      }
    }
  }, [authLoading, user, navigate]);

  useEffect(() => {
    if (user?.id && !authLoading) {
      fetchPayouts();
    }
  }, [user?.id, authLoading, filterStatus]);

  const fetchPayouts = async () => {
    setLoading(true);
    try {
      const response = await apiService.get(`/provider/payouts`);
      if (response.success && Array.isArray(response.data)) {
        let filtered = response.data;
        if (filterStatus !== 'all') {
          filtered = filtered.filter((p: Payout) => p.status === filterStatus);
        }
        setPayouts(filtered);
      }
    } catch (error) {
      console.error('Error fetching payouts:', error);
      toast({
        title: 'Error',
        description: 'Failed to load payouts',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleRequestPayout = async () => {
    try {
      const response = await apiService.post(`/provider/payouts/request`, {});
      toast({
        title: 'Success',
        description: response.message || 'Payout request submitted successfully',
      });
      fetchPayouts();
    } catch (error: any) {
      console.error('Error requesting payout:', error);
      toast({
        title: 'Unable to request payout',
        description: error?.message || 'Failed to request payout',
        variant: 'destructive',
      });
    }
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'paid':
        return <Badge className="bg-green-100 text-green-800">Paid</Badge>;
      case 'approved':
        return <Badge className="bg-blue-100 text-blue-800">Approved</Badge>;
      case 'processing':
        return <Badge className="bg-yellow-100 text-yellow-800">Processing</Badge>;
      case 'pending':
        return <Badge className="bg-gray-100 text-gray-800">Pending</Badge>;
      case 'failed':
        return <Badge className="bg-red-100 text-red-800">Failed</Badge>;
      default:
        return <Badge>{status}</Badge>;
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'paid':
        return <CheckCircle className="w-5 h-5 text-green-600" />;
      case 'pending':
      case 'processing':
        return <Clock className="w-5 h-5 text-yellow-600" />;
      default:
        return <Send className="w-5 h-5 text-gray-600" />;
    }
  };

  const totalPaid = payouts
    .filter(p => p.status === 'paid')
    .reduce((sum, p) => sum + p.amount_pence, 0) / 100;

  const totalPending = payouts
    .filter(p => p.status === 'pending' || p.status === 'processing')
    .reduce((sum, p) => sum + p.amount_pence, 0) / 100;

  const totalApproved = payouts
    .filter(p => p.status === 'approved')
    .reduce((sum, p) => sum + p.amount_pence, 0) / 100;

  if (authLoading) {
    return (
      <ProviderLayout>
        <div className="flex justify-center items-center min-h-screen">
          <div className="text-center">
            <Loader2 className="w-8 h-8 animate-spin text-blue-600 mx-auto mb-4" />
            <p className="text-gray-600">Authenticating...</p>
          </div>
        </div>
      </ProviderLayout>
    );
  }

  if (!user || user.role !== 'provider') {
    return (
      <ProviderLayout>
        <div className="flex justify-center items-center min-h-screen">
          <div className="text-center">
            <AlertCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
            <p className="text-gray-600 mb-4">Not authorized to access this page</p>
            <Button onClick={() => navigate('/provider/login')}>Go to Login</Button>
          </div>
        </div>
      </ProviderLayout>
    );
  }

  return (
    <ProviderLayout>
      <Helmet>
        <title>Payouts - VireWire Provider</title>
        <meta name="description" content="Manage your payouts and bank transfers" />
      </Helmet>

      <div className="space-y-8">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="space-y-2">
            <h1 className="text-3xl font-bold text-gray-900">Payouts</h1>
            <p className="text-gray-600">Track and manage your bank transfers and payout history.</p>
          </div>
          <Button onClick={handleRequestPayout} className="bg-green-600 hover:bg-green-700 text-white">
            <Send className="w-4 h-4 mr-2" />
            Request Payout
          </Button>
        </div>

        {/* Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Card>
            <CardContent className="p-6 space-y-2">
              <p className="text-xs font-bold text-gray-600 uppercase">Total Paid</p>
              <p className="text-3xl font-bold text-green-600">£{totalPaid.toFixed(2)}</p>
              <p className="text-xs text-gray-500">Successfully transferred</p>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-6 space-y-2">
              <p className="text-xs font-bold text-gray-600 uppercase">Pending Approval</p>
              <p className="text-3xl font-bold text-yellow-600">£{totalPending.toFixed(2)}</p>
              <p className="text-xs text-gray-500">Awaiting processing</p>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-6 space-y-2">
              <p className="text-xs font-bold text-gray-600 uppercase">Approved</p>
              <p className="text-3xl font-bold text-blue-600">£{totalApproved.toFixed(2)}</p>
              <p className="text-xs text-gray-500">Ready for transfer</p>
            </CardContent>
          </Card>
        </div>

        {/* Filter Section */}
        <div className="flex gap-2 items-center">
          <span className="text-sm font-semibold text-gray-700">Filter:</span>
          {['all', 'pending', 'approved', 'processing', 'paid', 'failed'].map(status => (
            <Button
              key={status}
              variant={filterStatus === status ? 'default' : 'outline'}
              size="sm"
              onClick={() => setFilterStatus(status)}
              className={filterStatus === status ? 'bg-green-600' : ''}
            >
              {status.charAt(0).toUpperCase() + status.slice(1)}
            </Button>
          ))}
        </div>

        {/* Payouts Table */}
        <Card>
          <CardContent className="p-6">
            {loading ? (
              <div className="flex justify-center items-center py-12">
                <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
              </div>
            ) : payouts.length === 0 ? (
              <div className="py-12 text-center text-gray-600">
                <p>No payouts found</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-gray-200">
                      <th className="text-left py-3 px-4 font-semibold text-gray-700">PAYOUT ID</th>
                      <th className="text-left py-3 px-4 font-semibold text-gray-700">AMOUNT</th>
                      <th className="text-left py-3 px-4 font-semibold text-gray-700">STATUS</th>
                      <th className="text-left py-3 px-4 font-semibold text-gray-700">REQUESTED DATE</th>
                      <th className="text-left py-3 px-4 font-semibold text-gray-700">PAID DATE</th>
                    </tr>
                  </thead>
                  <tbody>
                    {payouts.map((payout) => (
                      <tr key={payout.id} className="border-b border-gray-100 hover:bg-gray-50">
                        <td className="py-3 px-4 text-gray-900 font-semibold">#{payout.id}</td>
                        <td className="py-3 px-4 font-bold text-gray-900">£{payout.amount_gbp}</td>
                        <td className="py-3 px-4">
                          <div className="flex items-center gap-2">
                            {getStatusIcon(payout.status)}
                            {getStatusBadge(payout.status)}
                          </div>
                        </td>
                        <td className="py-3 px-4 text-gray-600">
                          {new Date(payout.created_at).toLocaleDateString('en-GB')}
                        </td>
                        <td className="py-3 px-4 text-gray-600">
                          {payout.paid_at
                            ? new Date(payout.paid_at).toLocaleDateString('en-GB')
                            : '-'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </ProviderLayout>
  );
};

export default ProviderPayoutsPage;
